import { Container, Step, StepButton, StepLabel, Stepper } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BrandEnum } from "../_common/enum/brand.enum";
import { IFormManage } from "../_common/models/form/formManage.model";
import { toastr } from "../_components/toastr/toastr";
import { getBrandHQCompletedLink } from "../_helpers/brand.helper";
import { getStringDateNormalized } from "../_helpers/dateTime.helper";
import { handleErrorResponse } from "../_helpers/handleResponse.helper";
import { retryHeight, sendContainerHeightToParent, sendContainerHeightToParent2, sendScrollMessageToParent } from "../_helpers/iFrameSize.helper";
import { formService } from "../_services/form.service";
import { FormSectionOriginalManage } from "./formSection/formSectionOriginal-manage";

export function FormOriginalManage(params: {
    brand: BrandEnum
}) {
    const navigate = useNavigate();

    const { formId, quoteId } = useParams<{ formId?: string; quoteId?: string }>();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const _formId = Number(formId);
    const _quoteId = !!quoteId ? Number(quoteId) : null;

    const [data, setData] = useState<IFormManage>();
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [activeStep, setActiveStep] = useState<number | null>(null);
    const [stepWithLatestId, setStepWithLatestId] = useState<number | null>(null);
    const hasSentScrollRef = useRef(false);
    const prevStepRef = useRef<number | null>(null);

    const effectiveFormId = token
        ? data?.id ?? _formId
        : _formId;

    // initial step
    useEffect(() => {
        setActiveStep(0);
    }, []);

    // load form data
    useEffect(() => {
        fetchData();
    }, [_formId, _quoteId]);

    // Latest desperate trial to fix resizing HQ first page
    useLayoutEffect(() => {
        if (!data || activeStep === null) return;

        requestAnimationFrame(() => {
            retryHeight(sendContainerHeightToParent);
        });
    }, [data, activeStep]);

    // first step
    useEffect(() => {
        if (!data) return;
        if (hasSentScrollRef.current) return;       // already scrolled once

        hasSentScrollRef.current = true;

        const id = setTimeout(() => {
            try {
                sendScrollMessageToParent();
            } catch {
                // ignore if no parent listener
            }
        }, 400); // after resize has had a chance to run otherwise it affects it

        return () => clearTimeout(id);
    }, [data]);

    // for other steps
    useEffect(() => {
        if (activeStep === null) return;

        const prev = prevStepRef.current;
        prevStepRef.current = activeStep;

        // First time we see a step, don't scroll (initial mount)
        if (prev === null) return;

        // No change - no scroll
        if (prev === activeStep) return;

        const id = setTimeout(() => {
            try {
                // Scroll inside the iframe
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });

                // Ask parent page to bring iframe into view
                sendScrollMessageToParent();
            } catch (e) {
                console.warn("[FORM ORIGINAL] scroll error", e);
            }
        }, 300); // small delay so step content has rendered

        return () => clearTimeout(id);
    }, [activeStep]);

    function fetchData(isOnSave?: boolean) {
        setLoading(true);
        console.log("token", token);

        const request = token
            ? formService.getByToken(token)
            : formService.get(_formId, _quoteId);

        return request
            .then((json) => {
                console.log("form hq data", json);

                setData(json);
              //  sendScrollMessageToParent();
            })
            .finally(() => {
                setLoading(false);
              //  retryHeight(sendContainerHeightToParent);

                if (isOnSave === true) {
                    setIsSaving(false);
                }
            });
    }

    function completeForm() {
        if (!effectiveFormId) {
            console.error("No effectiveFormId, cannot complete form");
            return;
        }

        setIsSaving(true);

        return formService
            .complete(effectiveFormId)
            .then(
                () => {
                    toastr.success("success");

                    const brand = params.brand;

                    if (!brand) {
                        console.error("No brand provided");
                        return;
                    }

                    const url = getBrandHQCompletedLink(brand);

                    try {
                        if (window.top && window.top !== window.self) {
                            window.top.location.href = url;
                        } else {
                            window.location.href = url;
                        }
                    } catch (e) {
                        console.warn("Top navigation blocked, falling back to iframe navigation", e);
                        window.location.href = url;
                    }
                },
                (err) => {
                    handleErrorResponse(err);
                }
            )
            .finally(() => {
                setIsSaving(false);
            });
    }


    function onSave() {
        if (data && activeStep !== null && activeStep === data.formSectionList.length - 1) {
            // last section: if save has succeeded complete the form
            completeForm();
        } else {
            // with review section - move to next step
            triggerNextStep(true);
        }
    }

    function triggerNextStep(isOnSave?: boolean) {
        if (!data || !data.formSectionList?.length) return;

        setActiveStep((prev) => {
            const current = prev ?? 0;
            const calculated = getActiveStep();

            const nextStep = current === calculated ? current + 1 : calculated;
            const maxIndex = data.formSectionList.length - 1;

            // make sure to never go past the last section
            const boundedNextStep = Math.min(nextStep, maxIndex);

            setStepWithLatestId((prevLatest) =>
                boundedNextStep > (prevLatest ?? -1) ? boundedNextStep : prevLatest
            );

            return boundedNextStep;
        });

        fetchData(isOnSave);
    }

    // no more review index, just first incomplete section, otherwise 0
    function getActiveStep() {
        let step: number | null = null;

        if (data && data.formSectionList.length > 0) {
            data.formSectionList.forEach((item, index) => {
                if (!item.id && step === null) {
                    step = index;
                }
            });
        }

        // default to first section
        return step ?? 0;
    }

    const isLastSection = !!data && activeStep !== null && activeStep === data.formSectionList.length - 1;

    return (
        <>
            <Container id="formContainer">
                {activeStep !== null && data && data.formSectionList.length > 0 && (
                    <>
                        <h2 style={{ textAlign: "center", marginBottom: "8px" }}>{data.displayName}</h2>

                        <div
                            style={{
                                width: "100%",
                                textAlign: "center",
                                marginBottom: "16px",
                            }}
                        >
                            <i>
                                Your progress to this point has been saved. You may access this form at
                                any time before submission.
                            </i>
                        </div>

                        {data.dateCompleted && (
                            <h6 style={{ width: "100%", textAlign: "center" }}>
                                Completed Date: {getStringDateNormalized(data.dateCompleted, true)}
                            </h6>
                        )}

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {data.formSectionList.map((listItem, index) => {
                                const stepProps: { completed?: boolean } = {};
                                stepProps.completed =
                                    (listItem.id || 0) > 0 || index < (activeStep ?? 0);

                                const disabled =
                                    stepWithLatestId === null ||
                                    (!listItem.id && (stepWithLatestId + 1) !== index);

                                return (
                                    <Step key={index + "_" + listItem.id} {...stepProps}>
                                        <StepButton
                                            disabled={disabled}
                                            onClick={() => {
                                                setActiveStep(index);
                                            }}
                                        >
                                            <StepLabel>{listItem.name}</StepLabel>
                                        </StepButton>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </>
                )}

                {activeStep !== null &&
                    activeStep !== data?.formSectionList.length && (
                        <FormSectionOriginalManage
                            formId={effectiveFormId ?? 0}
                            formTemplateSectionId={
                                data &&
                                    data.formSectionList.length > 0 &&
                                    activeStep < data.formSectionList.length
                                    ? data.formSectionList[activeStep].formTemplateSectionId
                                    : 0
                            }
                            isSectionSaving={isSaving}
                            formSectionId={data?.id}
                            onSave={onSave}
                            isLastSection={isLastSection}
                        />
                    )}
            </Container>
        </>
    );
}
